// nono-loop.js: media islands for theme-2026 (FERDIG «00 START HER» §8, FRAMEWORK §8.3 S-10b, D-145).
//   <nono-loop>   silent loop film (snippets/c-film.liquid); optional timed sentences (§8.8: data-sentences +
//                 [data-film-sentence] tile, switched by currentTime) and `once` (one round, then the poster;
//                 plays again from the start when the film comes back into view)
//   <nono-player> tap-to-play film with sound in a <dialog> (snippets/c-video-tile.liquid)
//   data-ladder   Shopify-hosted film (video setting): rendition picked by width, HLS first in the player
//   <nono-map>    click-to-load Google map (snippets/c-map.liquid)
// ES module, no dependencies. Nothing is fetched before the reader is near the media (or asks for it).

const base = (el, src) => (/^https?:|^\//.test(src) ? src : (el.dataset.base || '') + src);
const reduced = () => matchMedia('(prefers-reduced-motion: reduce)').matches || !!(navigator.connection && navigator.connection.saveData);
const parse = (s) => { try { return JSON.parse(s || 'null'); } catch { return null; } };
const num = (v) => parseFloat(String(v).replace(',', '.'));
/** Sentences: JSON [{text, from, to}] (s) or one «from-to|text» per line. from > to = across the loop point. */
function sentences(s) {
  s = (s || '').trim();
  if (!s) return [];
  const list = s[0] === '[' ? parse(s) || [] : s.split('\n').map((l) => {
    const i = l.indexOf('|');
    const m = i > 0 && /^\s*([\d.,]+)\s*[-–]\s*([\d.,]+)\s*$/.exec(l.slice(0, i));
    return m && { from: m[1], to: m[2], text: l.slice(i + 1) };
  });
  return list.filter((o) => o && o.text).map((o) => ({ text: String(o.text).trim(), from: num(o.from), to: num(o.to) }))
    .filter((o) => o.text && o.from >= 0 && o.to >= 0);
}

/** First playable source: AV1/HEVC must decode power-efficiently; H.264 only needs canPlayType. */
async function pick(video, list) {
  for (const s of list || []) {
    if (!s || !s.src || !video.canPlayType(s.type || '')) continue;
    if (/av01|hvc1|hev1/.test(s.type) && navigator.mediaCapabilities) {
      try {
        const info = await navigator.mediaCapabilities.decodingInfo({
          type: 'file',
          video: { contentType: s.type, width: s.w || 1080, height: s.h || 1080, bitrate: s.bitrate || 4e6, framerate: s.fps || 30 },
        });
        if (!info.supported || !info.powerEfficient) continue;
      } catch { continue; }
    }
    return s;
  }
  return null;
}

const HLS = /mpegurl/i;
/**
 * Shopify-hosted film (data-ladder, snippets/x-video-sources): the HLS playlist when the browser plays HLS
 * natively (players only list it), else the smallest progressive mp4 at least `cssW` × DPR (DPR capped at 2)
 * wide, else the largest. No mediaCapabilities pass: Shopify's renditions are H.264.
 */
function ladder(video, list, cssW) {
  const ok = (list || []).filter((s) => s && s.src && video.canPlayType(s.type || 'video/mp4'));
  const hls = ok.find((s) => HLS.test(s.type));
  if (hls) return hls;
  const mp4 = ok.filter((s) => !HLS.test(s.type)).sort((a, b) => (a.w || 0) - (b.w || 0));
  const want = cssW * Math.min(window.devicePixelRatio || 1, 2);
  return mp4.find((s) => (s.w || 0) >= want) || mp4[mp4.length - 1] || null;
}

let current = null; // the one loop that may play
const inView = new Set(); // loops ≥ 55 % visible, in the order they arrived
// Speed (21.10.2026): a film never competes with the first paint. Loops start fetching when the page has loaded,
// the browser is idle and at least 3 s have passed since navigation (the poster is the LCP), or at once on the
// reader's first touch, scroll or key. Measured: the 2 MB hero film was pushing mobile LCP to 9–18 s.
const MIN_MS = 3000;
let go = null;
const settled = new Promise((resolve) => {
  go = resolve;
  const idle = () => (window.requestIdleCallback ? requestIdleCallback(resolve, { timeout: 2000 }) : setTimeout(resolve, 200));
  const after = () => setTimeout(idle, Math.max(0, MIN_MS - performance.now()));
  if (document.readyState === 'complete') after(); else addEventListener('load', after, { once: true });
});
['pointerdown', 'keydown', 'scroll', 'touchstart'].forEach((t) => addEventListener(t, () => go(), { once: true, passive: true }));
const afterLoad = (fn) => { settled.then(fn); };

class NonoLoop extends HTMLElement {
  connectedCallback() {
    if (this.video) return;
    this.video = this.querySelector('video');
    this.btn = this.querySelector('.c-film__toggle');
    this.sources = parse(this.dataset.sources) || {};
    this.mq = matchMedia(this.dataset.media || '(min-width: 80em)');
    this.userPaused = false;
    this.attached = '';
    this.once = this.hasAttribute('once');
    if (this.once) this.video.loop = false;
    this.tile = this.querySelector('[data-film-sentence]');
    this.lines = this.tile ? sentences(this.dataset.sentences) : [];
    this.fallback = this.tile ? this.tile.dataset.fallback ?? this.tile.textContent : '';
    if (this.lines.length) this.video.addEventListener('timeupdate', () => this.sentence());
    this.video.addEventListener('ended', () => {
      this.done = true; // once: back to the poster until the film leaves the view and comes back
      if (current === this) current = null;
      this.state('poster');
    });
    this.btn.hidden = false;
    this.btn.addEventListener('click', () => this.toggle());
    this.video.addEventListener('playing', () => this.state('playing'));
    this.video.addEventListener('pause', () => this.attached && this.state('paused'));
    this.state('poster');
    if (reduced()) return; // poster only; the button loads the film on request
    afterLoad(() => {
      this.near = new IntersectionObserver((e) => { if (e[0].isIntersecting) this.attach(); }, { rootMargin: '600px' });
      this.seen = new IntersectionObserver((e) => {
        this.visible = e[0].intersectionRatio >= 0.55;
        if (this.visible) { inView.add(this); this.play(false); return; }
        inView.delete(this);
        const was = current === this;
        this.pause(false);
        if (this.once && this.attached) { this.done = false; this.video.currentTime = 0; } // re-entry restarts
        // The playing loop left: hand over to another loop that is still in view (≤ 1 plays at a time).
        if (was) for (const other of inView) { if (!other.userPaused && !other.blocked && !other.done) { other.play(false); break; } }
      }, { threshold: [0, 0.55] });
      this.near.observe(this);
      this.seen.observe(this);
    });
    this.mq.addEventListener('change', () => {
      if (!this.attached) return;
      const was = !this.video.paused;
      this.release();
      this.attach().then(() => was && this.play(false));
    });
  }

  disconnectedCallback() {
    inView.delete(this);
    this.near?.disconnect();
    this.seen?.disconnect();
    if (current === this) current = null;
  }

  state(s) {
    this.dataset.state = s;
    const playing = s === 'playing';
    this.btn.setAttribute('aria-label', playing ? this.dataset.labelPause : this.dataset.labelPlay);
    const poster = this.querySelector('.c-film__poster');
    if (poster) poster.toggleAttribute('aria-hidden', playing);
    if (this.lines?.length) {
      if (playing || s === 'paused') this.sentence();
      else this.say(this.fallback); // poster (reduced motion, no film yet, once ended): the static sentence
    }
  }

  /** The sentence for the current time, or none (the tile fades out between sentences). */
  sentence() {
    const t = this.video.currentTime;
    const l = this.lines.find((o) => (o.from <= o.to ? t >= o.from && t < o.to : t >= o.from || t < o.to));
    this.say(l ? l.text : '');
  }

  say(text) {
    if (text === this.shown) return;
    this.shown = text;
    if (text) this.tile.textContent = text;
    this.tile.classList.toggle('is-off', !text);
  }

  async attach() {
    const crop = this.mq.matches ? 'pc' : 'mobile';
    if (this.attached === crop) return true;
    this.near?.disconnect();
    const list = this.sources[crop] || this.sources.pc || this.sources.mobile;
    const s = this.hasAttribute('data-ladder') ? ladder(this.video, list, this.getBoundingClientRect().width || 1080) : await pick(this.video, list);
    if (!s) { this.state('poster'); return false; }
    this.video.src = base(this, s.src);
    if (s.w && s.h) { this.video.width = s.w; this.video.height = s.h; }
    this.attached = crop;
    return true;
  }

  release() {
    this.video.pause();
    this.video.removeAttribute('src');
    this.video.load();
    this.attached = '';
    this.state('poster');
  }

  async play(byUser) {
    if (!byUser && (this.userPaused || this.blocked || this.done)) return;
    this.done = false;
    if (!(await this.attach())) return;
    if (!byUser && !this.visible) return;
    if (current && current !== this) current.pause(false);
    current = this;
    try {
      await this.video.play();
    } catch (err) {
      if (err && err.name === 'NotAllowedError') { this.blocked = true; this.release(); }
    }
  }

  pause(byUser) {
    if (byUser) this.userPaused = true;
    if (current === this) current = null;
    if (this.attached) this.video.pause();
  }

  toggle() {
    if (this.dataset.state === 'playing') { this.pause(true); return; }
    this.userPaused = false;
    this.blocked = false;
    this.play(true);
  }
}

class NonoPlayer extends HTMLElement {
  connectedCallback() {
    // data-story: the tile belongs to a <nono-stories> row; its shared player (nono-stories.js) plays it.
    if (this.dialog || this.hasAttribute('data-story')) return;
    this.dialog = this.querySelector('dialog');
    this.opener = this.querySelector('[data-player-open]');
    this.video = this.dialog.querySelector('video');
    this.opener.addEventListener('click', () => this.open());
    // Speed (owner 19.10.2026): start fetching on the first sign of intent, not after the click. A pointer
    // resting on the tile (desktop) or the finger going down (touch) buys 100–300 ms before the click fires.
    let hover = 0;
    this.opener.addEventListener('pointerenter', (e) => {
      if (e.pointerType === 'mouse') hover = setTimeout(() => this.warm(), 120);
    });
    this.opener.addEventListener('pointerleave', () => clearTimeout(hover));
    this.opener.addEventListener('pointerdown', () => this.warm());
    this.dialog.querySelector('[data-player-close]').addEventListener('click', () => this.dialog.close());
    this.dialog.addEventListener('click', (e) => { if (e.target === this.dialog) this.dialog.close(); });
    this.dialog.addEventListener('close', () => { this.video.pause(); this.opener.focus(); });
    this.video.addEventListener('playing', () => this.dialog.removeAttribute('data-loading'));
    this.video.addEventListener('waiting', () => { if (this.dialog.open) this.dialog.setAttribute('data-loading', ''); });
  }

  /** Sets the source once (crop, captions, rendition) and lets the browser start buffering. */
  async warm() {
    if (this.video.currentSrc || this.warming) return this.warming;
    this.warming = (async () => {
      let l = parse(this.dataset.sources);
      const crop = innerWidth > innerHeight ? 'pc' : 'mobile';
      if (l && !Array.isArray(l)) l = l[crop] || l.pc || l.mobile; // band films (10 §01)
      // Captions per crop: keep the crop's own track (or the only one there is), drop the other.
      const tracks = this.video.querySelectorAll('track[data-crop]');
      const keep = [...tracks].find((t) => t.dataset.crop === crop) || tracks[0];
      tracks.forEach((t) => t !== keep && t.remove());
      let s;
      if (this.hasAttribute('data-ladder')) {
        // Progressive MP4 first: measured 19.10.2026 it shows the first frame 2–3× sooner than Shopify's HLS
        // (one request instead of playlist → variant → segment) and at full size from the first frame (HLS
        // starts at 480p). The HLS playlist stays as the fallback when no MP4 plays here.
        const mp4 = (l || []).filter((x) => x && !HLS.test(x.type || ''));
        const top = mp4.find((x) => x.w && x.h);
        const cssW = top ? Math.min(innerWidth, innerHeight * top.w / top.h) : innerWidth;
        s = ladder(this.video, mp4, cssW) || ladder(this.video, l, cssW);
      } else s = await pick(this.video, l);
      if (!s) return;
      this.video.preload = 'auto';
      this.video.src = base(this, s.src);
    })();
    return this.warming;
  }

  async open() {
    current?.pause(false);
    this.dialog.setAttribute('data-loading', '');
    this.dialog.showModal();
    await this.warm();
    this.video.play().catch(() => {
      this.dialog.removeAttribute('data-loading');
      this.video.setAttribute('controls', '');
    });
  }
}

class NonoMap extends HTMLElement {
  connectedCallback() {
    const trigger = this.querySelector('[data-map-load]');
    if (!trigger || this.done) return;
    trigger.setAttribute('role', 'button');
    trigger.addEventListener('click', (e) => { e.preventDefault(); this.load(); });
    trigger.addEventListener('keydown', (e) => { if (e.key === ' ') { e.preventDefault(); this.load(); } });
  }

  load() {
    if (this.done) return;
    this.done = true;
    const f = document.createElement('iframe');
    f.className = 'c-map__frame';
    f.src = this.dataset.src;
    f.title = this.dataset.title || '';
    if (this.dataset.label) f.setAttribute('aria-label', this.dataset.label);
    f.loading = 'lazy';
    f.referrerPolicy = 'no-referrer-when-downgrade';
    f.allowFullscreen = true;
    f.tabIndex = -1;
    this.querySelector('.c-map__box').append(f);
    this.dataset.state = 'embed';
    f.addEventListener('load', () => f.focus(), { once: true });
  }
}

customElements.get('nono-loop') || customElements.define('nono-loop', NonoLoop);
customElements.get('nono-player') || customElements.define('nono-player', NonoPlayer);
customElements.get('nono-map') || customElements.define('nono-map', NonoMap);
